/**
 * Note TOC Popover Wrapper
 *
 * Reusable popover component with mouse interaction handling
 * - Controlled open/close state
 * - Mouse enter/leave with delay to allow moving into popover
 * - Consistent styling across all TOC popover instances
 *
 * Used by:
 * - NoteTOC (hover interaction)
 * - NoteTOCButton (click interaction)
 */

import React from 'react';
import { Popover } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface NoteTOCPopoverProps {
  /**
   * Whether popover is open
   */
  open: boolean;

  /**
   * Anchor element for popover positioning
   */
  anchorEl: HTMLElement | null;

  /**
   * Close handler
   */
  onClose: () => void;

  /**
   * Mouse enter handler for popover paper
   * Used to keep popover open when mouse moves into it
   */
  onMouseEnter?: () => void;

  /**
   * Mouse leave handler for popover paper
   * Used to close popover when mouse leaves it
   */
  onMouseLeave?: () => void;

  /**
   * Content to display inside popover
   */
  children: React.ReactNode;

  /**
   * Anchor origin for popover positioning
   * @default { vertical: 'top', horizontal: 'center' }
   */
  anchorOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };

  /**
   * Transform origin for popover positioning
   * @default { vertical: 'top', horizontal: 'left' }
   */
  transformOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

/**
 * Popover wrapper for Note TOC
 * Provides consistent styling and mouse interaction handling
 */
export const NoteTOCPopover: React.FC<NoteTOCPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  onMouseEnter,
  onMouseLeave,
  children,
  anchorOrigin = {
    vertical: 'top',
    horizontal: 'center',
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'left',
  },
}) => {
  const theme = useTheme();

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      disableRestoreFocus
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      sx={{
        pointerEvents: 'none',
      }}
      slotProps={{
        paper: {
          onMouseEnter,
          onMouseLeave,
          sx: {
            pointerEvents: 'auto',
            boxShadow: theme.shadows[8],
            borderRadius: 1,
            minWidth: 280,
            maxHeight: 'min(600px, 85vh)',
            overflow: 'auto',
          },
        },
      }}
    >
      {children}
    </Popover>
  );
};
